/**
 * Definición de los campos del Registro de Anestesia — la MISMA estructura,
 * orden y etiquetas que el formulario que llena el anestesiólogo
 * (`AnesthesiaSignPage`).
 *
 * El formulario es "papel libre": todos los inputs escriben a un único mapa
 * `freeFormData` (Record<string,string>) que se persiste como jsonb. Sin este
 * catálogo, quien renderiza el registro guardado (impresión / vista de solo
 * lectura) solo vería claves crudas tipo `cirugiaARealizar` y perdería el
 * agrupamiento. Con él, el documento se reconstruye tal cual se llenó.
 *
 * IMPORTANTE: si agregas/renombras un campo en `AnesthesiaSignPage`, refléjalo
 * aquí — si no, el campo se seguirá guardando pero saldrá bajo "Otros datos"
 * (el render tiene un catch-all para no perder nada).
 */

export interface AnesthesiaFieldDef {
  readonly key: string;
  readonly label: string;
}

export interface AnesthesiaGroupDef {
  /** Subtítulo dentro de la sección (opcional). */
  readonly titulo?: string;
  readonly campos: readonly AnesthesiaFieldDef[];
}

export interface AnesthesiaSectionDef {
  readonly titulo: string;
  readonly grupos: readonly AnesthesiaGroupDef[];
}

/** Medicamentos de sedación: claves dinámicas `medicamentoSedacion1..N`. */
export const MED_SEDACION_RE = /^medicamentoSedacion(\d+)$/;

export const ANESTHESIA_SECTIONS: readonly AnesthesiaSectionDef[] = [
  {
    titulo: 'A. Identificación',
    grupos: [
      {
        campos: [
          { key: 'fechaHoy', label: 'Fecha de hoy' },
          { key: 'fecha', label: 'Fecha del procedimiento' },
          { key: 'profesional', label: 'Profesional que realiza el procedimiento' },
          { key: 'identificacion', label: 'Identificación' },
          { key: 'apellidos', label: 'Apellidos' },
          { key: 'nombres', label: 'Nombres' },
          { key: 'fechaNacimiento', label: 'Fecha de nacimiento' },
          { key: 'edad', label: 'Edad' },
          { key: 'sexo', label: 'Sexo' },
          { key: 'ocupacion', label: 'Ocupación' },
          { key: 'direccion', label: 'Dirección' },
          { key: 'barrio', label: 'Barrio' },
          { key: 'entidad', label: 'Entidad' },
          { key: 'cirugiaARealizar', label: 'Cirugía a realizar' },
          { key: 'cama', label: 'Cama' },
        ],
      },
    ],
  },
  {
    titulo: 'B. Registro de anestesia',
    grupos: [
      {
        campos: [
          { key: 'paciente', label: 'Paciente' },
          { key: 'edadCab', label: 'Edad' },
          { key: 'sexoCab', label: 'Sexo' },
          { key: 'cirugiaProg', label: 'Cirugía PROG.' },
          { key: 'urg', label: 'URG.' },
          { key: 'emp', label: 'EMP.' },
          { key: 'hc', label: 'H.C.' },
          { key: 'camaCab', label: 'Cama' },
          { key: 'urgenciaCab', label: 'Urgencia' },
          { key: 'fechaCab', label: 'Fecha' },
          { key: 'mCab', label: 'M' },
          { key: 'aCab', label: 'A' },
          { key: 'salaCab', label: 'Sala' },
          { key: 'intervencion', label: 'Intervención' },
          { key: 'cups', label: 'CUPS' },
          { key: 'diag', label: 'Diagnóstico' },
          { key: 'cod', label: 'Código diagnóstico' },
          { key: 'anestesiologos', label: 'Anestesiólogo(s)' },
          { key: 'cirujanos', label: 'Cirujano(s)' },
        ],
      },
      {
        titulo: 'Anestesia — General',
        campos: [
          { key: 'inhalatoria', label: 'Inhalatoria' },
          { key: 'iv', label: 'IV' },
          { key: 'nla', label: 'NLA' },
        ],
      },
      {
        titulo: 'Anestesia — Regional',
        campos: [
          { key: 'epidural', label: 'Epidural' },
          { key: 'espinal', label: 'Espinal' },
          { key: 'nivelPuncion', label: 'Nivel de punción' },
          { key: 'cateter', label: 'Catéter' },
          { key: 'nivelAnestesia', label: 'Nivel anestesia' },
          { key: 'localCont', label: 'Local cont.' },
          { key: 'troncul', label: 'Troncular' },
          { key: 'plexual', label: 'Plexual' },
        ],
      },
      {
        titulo: 'Monitoreo',
        campos: [
          { key: 'ecg', label: 'ECG' },
          { key: 'derivacion', label: 'Derivación' },
          { key: 'pvc', label: 'PVC' },
          { key: 'viaPvc', label: 'Vía (PVC)' },
          { key: 'cap', label: 'CAP' },
          { key: 'viaCap', label: 'Vía (CAP)' },
          { key: 'estNPeriferico', label: 'Est. N. Periférico' },
        ],
      },
    ],
  },
  {
    titulo: 'C. Gráfica de signos vitales',
    grupos: [
      {
        campos: [
          { key: 'horaInduccion', label: 'Hora inducción' },
          { key: 'rapida', label: 'Rápida' },
          { key: 'tet', label: 'TET #' },
          { key: 'ot', label: 'OT' },
          { key: 'nt', label: 'NT' },
          { key: 'otrasIntubacion', label: 'Otras (intubación)' },
          { key: 'ventilacionCont', label: 'Ventilación CONT.' },
          { key: 'ventilacionAsist', label: 'Ventilación ASIST.' },
          { key: 'notasGrafica', label: 'Notas' },
          { key: 'posicion', label: 'Posición' },
        ],
      },
    ],
  },
  {
    titulo: 'D. Balance de líquidos',
    grupos: [
      {
        campos: [
          { key: 'ayunoHoras', label: 'Ayuno (horas)' },
          { key: 'ayunoMl', label: 'Ayuno (ml)' },
          { key: 'mantenimientoMl', label: 'Mantenimiento (ml)' },
          { key: 'volemiaMl', label: 'Volemia (ml)' },
          { key: 'pps', label: 'PPS' },
        ],
      },
      {
        titulo: 'Total líquidos administrados',
        campos: [
          { key: 'cristaloides', label: 'Cristaloides (ml)' },
          { key: 'coloides', label: 'Coloides (ml)' },
          { key: 'sangre', label: 'Sangre (ml)' },
          { key: 'plasma', label: 'Plasma (ml)' },
          { key: 'globulosRojos', label: 'Glóbulos rojos (u)' },
          { key: 'otrosAdministrados', label: 'Otros (administrados)' },
          { key: 'balance', label: 'Balance' },
        ],
      },
      {
        titulo: 'Eliminados',
        campos: [
          { key: 'diuresisMl', label: 'Diuresis (ml)' },
          { key: 'diuresisRatio', label: 'ml/kg/hora' },
          { key: 'sangradoMl', label: 'Sangrado (ml)' },
          { key: 'otrosEliminados', label: 'Otros (eliminados)' },
        ],
      },
    ],
  },
  {
    titulo: 'E. Cierre del registro',
    grupos: [
      {
        campos: [
          { key: 'complicacionesObservaciones', label: 'Complicaciones y observaciones' },
          { key: 'trasladoRecuperacion', label: 'Traslado a recuperación' },
          { key: 'trasladoUci', label: 'Traslado a UCI' },
          { key: 'trasladoOtros', label: 'Otros (traslado)' },
        ],
      },
    ],
  },
];

/** Todas las claves con etiqueta conocida (para detectar las no mapeadas). */
export const ANESTHESIA_KNOWN_KEYS: ReadonlySet<string> = new Set(
  ANESTHESIA_SECTIONS.flatMap((s) => s.grupos.flatMap((g) => g.campos.map((c) => c.key))),
);

/* -------------------------------------------------------------------------- */
/* Versionado del formulario                                                   */
/* -------------------------------------------------------------------------- */

/**
 * El formato del Registro de Anestesia cambió (nuevo papel Endomedical). Para
 * NO alterar los registros ya firmados, cada registro guarda dentro de
 * `freeFormData` una marca de versión (`formVersion`). Los registros previos no
 * la tienen → se tratan como v1 y se renderizan con el catálogo/gráfica viejos,
 * exactamente como se llenaron. Los nuevos usan v2 (el formato del PDF nuevo).
 */
export const ANESTHESIA_FORM_VERSION_KEY = 'formVersion';
export const CURRENT_ANESTHESIA_FORM_VERSION = '2';

/** Catálogo del formato NUEVO (v2) — solo los campos del papel actual. */
export const ANESTHESIA_SECTIONS_V2: readonly AnesthesiaSectionDef[] = [
  {
    titulo: 'Registro de anestesia',
    grupos: [
      {
        campos: [
          { key: 'fecha', label: 'Fecha' },
          { key: 'nombrePaciente', label: 'Nombre' },
          { key: 'identificacion', label: 'Identificación' },
          { key: 'edad', label: 'Edad' },
          { key: 'empresa', label: 'Empresa' },
          { key: 'riesgo', label: 'Riesgo' },
          { key: 'procedimiento', label: 'Procedimiento' },
          { key: 'cirujano', label: 'Cirujano' },
          { key: 'anestesiologo', label: 'Anestesiólogo' },
        ],
      },
      {
        titulo: 'Técnica anestésica',
        campos: [{ key: 'tecnicaAnestesica', label: 'Técnica anestésica' }],
      },
      {
        titulo: 'Observaciones',
        campos: [{ key: 'observaciones', label: 'Observaciones' }],
      },
    ],
  },
];

const ANESTHESIA_KNOWN_KEYS_V2: ReadonlySet<string> = new Set(
  ANESTHESIA_SECTIONS_V2.flatMap((s) => s.grupos.flatMap((g) => g.campos.map((c) => c.key))),
);

/** Lee la versión del formato guardada en el registro (`'1'` por defecto). */
export function anesthesiaFormVersion(data: Record<string, unknown>): string {
  const v = data[ANESTHESIA_FORM_VERSION_KEY];
  return typeof v === 'string' ? v : '1';
}

/** Devuelve el catálogo de secciones correcto según la versión del registro. */
export function sectionsForData(
  data: Record<string, unknown>,
): readonly AnesthesiaSectionDef[] {
  return anesthesiaFormVersion(data) === '2' ? ANESTHESIA_SECTIONS_V2 : ANESTHESIA_SECTIONS;
}

/** Claves con etiqueta conocida para la versión del registro. */
export function knownKeysForData(data: Record<string, unknown>): ReadonlySet<string> {
  return anesthesiaFormVersion(data) === '2' ? ANESTHESIA_KNOWN_KEYS_V2 : ANESTHESIA_KNOWN_KEYS;
}

/** Variante de la gráfica ('v2' para el formato nuevo, 'v1' para los previos). */
export function chartVariantForData(data: Record<string, unknown>): 'v1' | 'v2' {
  return anesthesiaFormVersion(data) === '2' ? 'v2' : 'v1';
}

/** Convierte una clave camelCase suelta en una etiqueta legible. */
export function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Medicamentos de sedación escritos por el anestesiólogo, en orden. */
export function medicamentosSedacion(data: Record<string, unknown>): string[] {
  return Object.keys(data)
    .filter((k) => MED_SEDACION_RE.test(k))
    .sort(
      (a, b) => Number(MED_SEDACION_RE.exec(a)?.[1]) - Number(MED_SEDACION_RE.exec(b)?.[1]),
    )
    .map((k) => data[k])
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '');
}
